import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { saveUserPreference } from "../userPreferences";

type DarkModeContextType = {
  dark: boolean;
  setDark: Dispatch<SetStateAction<boolean>>;
};

const DarkModeContext = createContext<DarkModeContextType | undefined>(
  undefined
);

export const DarkModeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();

  const getInitialTheme = () => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  };

  const [dark, setDarkState] = useState(getInitialTheme);
  const syncedUserIdRef = useRef<string | undefined>(undefined);

  // Apply cloud preference on login
  useEffect(() => {
    if (!user || user.id === syncedUserIdRef.current) return;
    syncedUserIdRef.current = user.id;
    const cloudTheme = user.user_metadata?.pref_theme as string | undefined;
    if (cloudTheme === "dark" || cloudTheme === "light") {
      setDarkState(cloudTheme === "dark");
    }
  }, [user]);

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [dark]);

  const setDark = useCallback(
    (value: SetStateAction<boolean>) => {
      const next = typeof value === "function" ? value(dark) : value;
      setDarkState(next);
      if (user) saveUserPreference("pref_theme", next ? "dark" : "light");
    },
    [dark, user]
  );

  return (
    <DarkModeContext.Provider value={{ dark, setDark }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context)
    throw new Error("useDarkMode must be used inside DarkModeProvider");
  return context;
};
